import { POLLY_LANGUAGE_OPTIONS, DEFAULT_LANGUAGE_KEY } from '../../../data/pollyVoices';
import { HintTooltip } from '../../HintTooltip';
import type { VoiceSelections } from '../../../utils/voiceSettings';

/**
 * 放送言語ごとの音声 (Polly VoiceId) 選択欄 — Issue #639。
 *
 * 日本語の話者は放送地点の「音声（任意）」(`voice_id`) が担うため、ここでは
 * **日本語以外で放送する言語** ごとに、その言語で使える話者だけを選択肢として出す
 * (英語の放送に日本語話者を設定するような誤りは選択肢の時点で作れない)。
 *
 * - 「（未選択 / 既定音声を使用）」はその言語の既定話者 (マスタ先頭) で放送する。
 * - 話者の選択肢が 1 つしかない言語 (中国語・韓国語など) も、明示的に分かるよう表示する。
 * - 日本語以外の放送言語が無ければ何も表示しない (従来の画面と同じ)。
 */
export function LocalizedVoiceFields({
  languages,
  value,
  onChange,
  testIdPrefix = 'voice-id-select',
}: {
  /** 放送地点で放送される言語 (コンテンツの `languages` の和集合)。 */
  languages: readonly string[];
  /** 現在の言語別話者 (`言語キー → VoiceId`)。 */
  value?: VoiceSelections;
  onChange: (value: VoiceSelections) => void;
  /** `data-testid` の接頭辞。言語キーを `-` で連結する。 */
  testIdPrefix?: string;
}): React.JSX.Element | null {
  const selectedLanguages = POLLY_LANGUAGE_OPTIONS.filter(
    (language) => language.key !== DEFAULT_LANGUAGE_KEY && languages.includes(language.key),
  );
  if (selectedLanguages.length === 0) return null;

  const selections: VoiceSelections = { ...value };
  return (
    <details open style={{ margin: '8px 0', minWidth: 0 }} data-testid={`${testIdPrefix}-group`}>
      <summary>
        各言語の音声（任意）
        <HintTooltip label="各言語の音声の説明" testId={`${testIdPrefix}-group-hint`}>
          放送言語ごとに Polly の合成音声 (声色) を選べます。 未選択の言語はその言語の既定の音声で
          放送されます。
        </HintTooltip>
      </summary>
      {selectedLanguages.map((language) => (
        <label key={language.key} style={{ display: 'block', marginTop: 8 }}>
          <span>{language.label}</span>
          <select
            data-testid={`${testIdPrefix}-${language.key}`}
            aria-label={`音声 ${language.label}`}
            style={{ width: '100%', boxSizing: 'border-box' }}
            value={selections[language.key] ?? ''}
            onChange={(event) => onChange({ ...selections, [language.key]: event.target.value })}
          >
            <option value="">（未選択 / 既定音声を使用）</option>
            {language.voices.map((voice) => (
              <option key={voice.id} value={voice.id}>
                {voice.label}
              </option>
            ))}
          </select>
        </label>
      ))}
    </details>
  );
}
